// src/pages/services/ServiceCreate.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Chip,
  FormHelperText,
  Snackbar,
  Alert,
  CircularProgress,
  Divider,
  FormControlLabel,
  Switch,
  Checkbox,
  FormGroup,
  Link
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Save as SaveIcon, ArrowBack as ArrowBackIcon, Add as AddIcon } from '@mui/icons-material';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';

const daysOfWeek = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes'];

const ServiceCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    nombre: '',
    descripción: '',
    fechaInicio: new Date(),
    fechaFin: new Date(),
    horario: '',
    lugar: '',
    precio: '',
    plazas: '',
    responsable: '',
    cursos: [],
    active: true,
    inscritos: 0,
    diasSemana: [],
    linkProveedor: ''
  });

  const [cursoInput, setCursoInput] = useState('');
  const [formErrors, setFormErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'precio' || name === 'plazas') {
      const regex = name === 'precio' ? /^$|^[0-9]+(\.[0-9]{0,2})?$/ : /^$|^[0-9]+$/;
      if (regex.test(value)) {
        setFormData({ ...formData, [name]: value });
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
    if (formErrors[name]) {
      setFormErrors({ ...formErrors, [name]: '' });
    }
  };

  const handleDateChange = (date, field) => {
    setFormData({ ...formData, [field]: date });
    if (formErrors[field]) {
      setFormErrors({ ...formErrors, [field]: '' });
    }
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    setFormData({ ...formData, [name]: checked });
  };

  const handleAddCurso = () => {
    if (!cursoInput.trim()) {
      setFormErrors({ ...formErrors, cursos: 'El curso no puede estar vacío' });
      return;
    }
    if (!formData.cursos.includes(cursoInput)) {
      setFormData({ ...formData, cursos: [...formData.cursos, cursoInput] });
    }
    setCursoInput('');
    if (formErrors.cursos) {
      setFormErrors({ ...formErrors, cursos: '' });
    }
  };

  const handleRemoveCurso = (curso) => {
    setFormData({ ...formData, cursos: formData.cursos.filter(c => c !== curso) });
  };

  const handleCheckboxChange = (day) => {
    const updatedDias = formData.diasSemana.includes(day)
      ? formData.diasSemana.filter(d => d !== day)
      : [...formData.diasSemana, day];
    setFormData({ ...formData, diasSemana: updatedDias });
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.nombre) errors.nombre = 'El nombre es obligatorio';
    if (!formData.fechaInicio) errors.fechaInicio = 'La fecha de inicio es obligatoria';
    if (!formData.fechaFin) errors.fechaFin = 'La fecha de fin es obligatoria';
    if (formData.fechaInicio > formData.fechaFin) errors.fechaFin = 'La fecha de fin debe ser posterior a la fecha de inicio';
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setError('');

    try {
      const serviceData = {
        ...formData,
        precio: formData.precio ? parseFloat(formData.precio) : 0,
        plazas: formData.plazas ? parseInt(formData.plazas, 10) : null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
      await addDoc(collection(db, 'services'), serviceData);
      setShowSuccess(true);
      setTimeout(() => navigate('/admin/services'), 2000);
    } catch (error) {
      console.error('Error creating service:', error);
      setError('Error al crear el servicio. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h5">Nuevo Servicio</Typography>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/services')}>
            Volver
          </Button>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField fullWidth label="Nombre del servicio" name="nombre" value={formData.nombre} onChange={handleChange} error={!!formErrors.nombre} helperText={formErrors.nombre} required />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Descripción" name="descripción" value={formData.descripción} onChange={handleChange} multiline rows={3} />
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePicker label="Fecha de inicio" value={formData.fechaInicio} onChange={(date) => handleDateChange(date, 'fechaInicio')} slotProps={{ textField: { fullWidth: true, required: true, error: !!formErrors.fechaInicio, helperText: formErrors.fechaInicio } }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <DatePicker label="Fecha de fin" value={formData.fechaFin} onChange={(date) => handleDateChange(date, 'fechaFin')} slotProps={{ textField: { fullWidth: true, required: true, error: !!formErrors.fechaFin, helperText: formErrors.fechaFin } }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Horario" name="horario" value={formData.horario} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Lugar" name="lugar" value={formData.lugar} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Responsable" name="responsable" value={formData.responsable} onChange={handleChange} />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControlLabel control={<Switch checked={formData.active} onChange={handleSwitchChange} name="active" />} label="Servicio activo" />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Precio (€)" name="precio" value={formData.precio} onChange={handleChange} InputProps={{ startAdornment: <Typography sx={{ mr: 1 }}>€</Typography> }} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Plazas disponibles" name="plazas" value={formData.plazas} onChange={handleChange} />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Días de la semana</Typography>
              <FormGroup row>
                {daysOfWeek.map((day) => (
                  <FormControlLabel key={day} control={<Checkbox checked={formData.diasSemana.includes(day)} onChange={() => handleCheckboxChange(day)} />} label={day} />
                ))}
              </FormGroup>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Enlace del proveedor" name="linkProveedor" value={formData.linkProveedor} onChange={handleChange} placeholder="https://proveedor.com" />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>Cursos a los que aplica</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextField label="Añadir curso" value={cursoInput} onChange={(e) => setCursoInput(e.target.value)} error={!!formErrors.cursos} helperText={formErrors.cursos} sx={{ mr: 1, flexGrow: 1 }} />
                <Button variant="outlined" onClick={handleAddCurso} startIcon={<AddIcon />}>Añadir</Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.cursos.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">No hay cursos añadidos, se aplicará a todos los cursos</Typography>
                ) : (
                  formData.cursos.map((curso, index) => (
                    <Chip key={index} label={curso} onDelete={() => handleRemoveCurso(curso)} />
                  ))
                )}
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
                <Button variant="outlined" onClick={() => navigate('/admin/services')} disabled={loading}>Cancelar</Button>
                <Button type="submit" variant="contained" startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />} disabled={loading}>Guardar</Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>

      <Snackbar open={showSuccess} autoHideDuration={4000} onClose={() => setShowSuccess(false)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="success">Servicio creado exitosamente</Alert>
      </Snackbar>

      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error">{error}</Alert>
      </Snackbar>
    </Box>
  );
};

export default ServiceCreate;
